import React from 'react';
import { Link } from 'components/core';

import { useKioskPro } from 'src/hooks/use-kiosk-pro';

import usePaths from 'hooks/use-paths';
import useUI from 'shared/hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

import { Container, ListHeader, ListHeaderText, ListHeaderLink, ListContainer } from './buy-it-again-list.styles';

type BuyItAgainListProps = {
  children: React.ReactNode;
  handleViewAllClick: (destinationUrl: string) => void;
  tabControls?: React.ReactNode;
};

const listPadding = 20;

/** Layout wrapper providing the "Buy it again" card shell with header and view-all link. */
export const BuyItAgainList = React.forwardRef(
  ({ children, handleViewAllClick, tabControls }: BuyItAgainListProps, ref): JSX.Element | null => {
    const UI = useUI();
    const { dispensary } = useDispensary();
    const { href } = usePaths({ dispensary });

    const isKioskProEnabled = useKioskPro();
    const hideViewAllOrders = UI.isKiosk && isKioskProEnabled;

    const hasUserPaths = UI.isEmbedded || UI.isStoreFront;
    const accountOrderHistoryPath = hasUserPaths ? `${href}/user/orders` : '/account/orders';

    return (
      <Container
        data-testid='buy-it-again-list'
        ref={ref}
        role='group'
        aria-roledescription='slide'
        aria-label='Buy it again'
      >
        <ListHeader padding={listPadding}>
          <ListHeaderText tag='h3' size='xsmall' weight='semi'>
            Buy it again
          </ListHeaderText>
          {!hideViewAllOrders && (
            <Link href={accountOrderHistoryPath}>
              <ListHeaderLink
                href={accountOrderHistoryPath}
                onClick={() => handleViewAllClick(accountOrderHistoryPath)}
              >
                View my orders
              </ListHeaderLink>
            </Link>
          )}
        </ListHeader>
        {tabControls}
        <ListContainer>{children}</ListContainer>
      </Container>
    );
  }
);
